import {
  Navigate,
  Outlet,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";

import { Checkout } from "@/components/Checkout/Steps/Checkout/Checkout";
import { NumberPorting } from "@/components/Checkout/Steps/NumberPorting/NumberPorting";
import { RateSelection } from "@/components/Checkout/Steps/RateSelection/RateSelection";
import { SimSelection } from "@/components/Checkout/Steps/SimSelection/SimSelection";
import { SimVerification } from "@/components/Checkout/Steps/SimVerification/SimVerification";
import { AddDevice } from "@/components/Portal/AddDevice/AddDevice";
import { AddDeviceSuccess } from "@/components/Portal/AddDevice/Success/AddDeviceSuccess";
import { Cockpit } from "@/components/Portal/Cockpit/Cockpit";
import { CockpitDashboard } from "@/components/Portal/Cockpit/CockpitDashboard/CockpitDashboard";
import { IdCheckPage } from "@/components/Portal/Cockpit/IdCheck/IdCheckPage";
import { DetailsDisplay } from "@/components/Portal/DeviceManagement/Details/Details";
import { DeviceManagement } from "@/components/Portal/DeviceManagement/DeviceManagement";
import { AddRoamingOption } from "@/components/Portal/DeviceManagement/Roaming/AddRoamingOption/AddRoamingOption";
import { CheckoutRoamingOption } from "@/components/Portal/DeviceManagement/Roaming/CheckoutRoamingOption/CheckoutRoamingOption";
import { ShowRoamingOptions } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/ShowRoamingOptions";
import { SubscriptionAutoRenewal } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionAutoRenewal/SubscriptionAutoRenewal.tsx";
import { SubscriptionCheckout } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/Steps/SubscriptionCheckout";
import { SubscriptionRateSelection } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/Steps/SubscriptionRateSelection";
import { SubscriptionChange } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/SubscriptionChange";
import { SubscriptionDisplay } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionDisplay";
import { VoiceSettings } from "@/components/Portal/DeviceManagement/VoiceSettings/VoiceSettings";
import { AddWatchSimOption } from "@/components/Portal/DeviceManagement/WatchSim/AddWatchSimOption/AddWatchSimOption";
import { ChangeWatchSimRenewal } from "@/components/Portal/DeviceManagement/WatchSim/ChangeWatchSimRenewal/ChangeWatchSimRenewal";
import { ShowWatchSimOptions } from "@/components/Portal/DeviceManagement/WatchSim/ShowWatchSimOptions/ShowWatchSimOptions";
import { BulkMoveSubscriptions } from "@/components/Portal/Documents/BillingAccounts/BulkMoveSubscriptions/BulkMoveSubscriptions";
import { BillList } from "@/components/Portal/Documents/BillList/BillList";
import { Documents } from "@/components/Portal/Documents/Documents";
import { ItemizedBillOverview } from "@/components/Portal/Documents/ItemizedBillOverview/ItemizedBillOverview";
import { PaymentMethods } from "@/components/Portal/Documents/PaymentMethods/PaymentMethods";
import { MySubscriptions } from "@/components/Portal/MySubscriptions/MySubscriptions";
import { MySubscriptionsList } from "@/components/Portal/MySubscriptions/MySubscriptionsList";
import { Inbox } from "@/components/Portal/UserAccount/Inbox/Inbox";
import { TwoFactorAuthStatus } from "@/components/Portal/UserAccount/Security/TwoFactorAuthStatus";
import { TwoFactorChangeProtection } from "@/components/Portal/UserAccount/TwoFactorChangeProtection";
import { useCustomerTribe } from "@/hooks/useCustomerTribe";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { BulkSimActivation } from "@/views/Portal/BulkSimActivation/BulkSimActivation";
import CreateGroup from "@/views/Portal/FriendsWithBenefits/CreateGroup/CreateGroup.tsx";
import GroupOverview from "@/views/Portal/FriendsWithBenefits/GroupOverview/GroupOverview.tsx";
import { PersonalDataOnboarding } from "@/views/Portal/PersonalDataOnboarding/PersonalDataOnboarding";
import { PersonalDetails } from "@/views/Portal/UserAccount/PersonalDetailsView";
import { TwoFactorChangeAppView } from "@/views/Portal/UserAccount/Security/change/TwoFactorChangeAppView";
import { TwoFactorChangeSmsView } from "@/views/Portal/UserAccount/Security/change/TwoFactorChangeSmsView";
import { TwoFactorInitialAppSetupView } from "@/views/Portal/UserAccount/Security/initialSetup/TwoFactorInitialAppSetupView";
import { TwoFactorInitialSmsConfirmView } from "@/views/Portal/UserAccount/Security/initialSetup/TwoFactorInitialSmsConfirmView";
import { TwoFactorInitialSmsSetupView } from "@/views/Portal/UserAccount/Security/initialSetup/TwoFactorInitialSmsSetupView";
import { SecurityView } from "@/views/Portal/UserAccount/Security/SecurityView";
import { TwoFactorBackupConfirmView } from "@/views/Portal/UserAccount/Security/shared/TwoFactorBackupConfirmView";
import { TwoFactorConfirmNumberChange } from "@/views/Portal/UserAccount/Security/shared/TwoFactorConfirmNumberChange";
import { TwoFactorConfirmSmsView } from "@/views/Portal/UserAccount/Security/shared/TwoFactorConfirmSmsView.tsx";
import { TwoFactorConfirmStandardView } from "@/views/Portal/UserAccount/Security/shared/TwoFactorConfirmStandardView";
import { TwoFactorSetupAppView } from "@/views/Portal/UserAccount/Security/TwoFactorSetupAppView";
import { TwoFactorSetupSmsView } from "@/views/Portal/UserAccount/Security/TwoFactorSetupSmsView";
import { TwoFactorInitialSetupView } from "@/views/Portal/UserAccount/TwoFactorInitialSetupView";
import { UserAccountView } from "@/views/Portal/UserAccount/UserAccountView";
import { InitialPasswordSetup } from "@/views/WelcomeScreen/InitialPasswordSetup";

import { AgentOnlyRoute } from "./AgentOnlyRoute";
import { NavigateWithQueryParams } from "./NavigateWithQueryParams";

/**
 * Forwards legacy `/portal/cockpit/manage/*` URLs to the new top-level
 * `/portal/manage/*` location, preserving the sub-path and query string so
 * existing bookmarks and deep links keep working after the section moved out
 * of the Cockpit route.
 */
const LegacyManageRedirect = () => {
  const { "*": rest } = useParams();
  const { search } = useLocation();
  const suffix = rest ? `/${rest}` : "";

  return (
    <Navigate
      to={`${portalRoutes.manageDevice.base}${suffix}${search}`}
      replace
    />
  );
};

/**
 * Forwards legacy `/portal/manage/tariff/*` URLs to the renamed
 * `/portal/manage/subscription/*` location, preserving the remaining sub-path
 * and query string so existing bookmarks and deep links keep working after the
 * `tariff` route segment was renamed to `subscription`.
 */
const LegacyTariffRedirect = () => {
  const { "*": rest } = useParams();
  const { search } = useLocation();
  const suffix = rest ? `/${rest}` : "";

  return (
    <Navigate
      to={`${portalRoutes.manageDevice.subscription.base}${suffix}${search}`}
      replace
    />
  );
};

/**
 * Forwards legacy `/portal/manage/subscription/checkout` payment-return URLs
 * to the actual checkout at `/portal/manage/subscription/change/checkout`,
 * preserving the query string (e.g. `?payment=success`) so SubscriptionCheckout
 * receives the payment gateway callback correctly.
 */
const LegacySubscriptionCheckoutRedirect = () => {
  const { search } = useLocation();

  return (
    <Navigate
      to={`${portalRoutes.manageDevice.subscription.checkout}${search}`}
      replace
    />
  );
};

const FriendsWithBenefitsRoute = () => {
  const { data: customerTribe } = useCustomerTribe();

  if (customerTribe?.tribeId) {
    return <Outlet />;
  }

  return <Navigate to={portalRoutes.friendsWithBenefits.createGroup} replace />;
};

export const portalRouter = [
  <Route
    key="index"
    index
    element={<Navigate to={portalRoutes.cockpit.base} replace />}
  />,
  <Route
    key={portalRoutes.cockpit.base}
    path={portalRoutes.cockpit.base}
    element={<Cockpit />}
  >
    <Route index element={<CockpitDashboard />} />
    <Route path="id-check" element={<IdCheckPage />} />
  </Route>,
  /*
    DeviceManagement now lives in its own top-level `/portal/manage` section
    (mirrors `addDevice`) instead of being nested under `/portal/cockpit`.
    This redirect keeps old `/portal/cockpit/manage/*` links working by
    forwarding them to the new location, path remainder and query preserved.
  */
  <Route
    key="cockpit-manage-redirect"
    path={`${portalRoutes.cockpit.base}/manage/*`}
    element={<LegacyManageRedirect />}
  />,
  /*
    The `tariff` section was renamed to `subscription`. This redirect keeps old
    `/portal/manage/tariff/*` links working by forwarding them to the new
    location, path remainder and query preserved.
  */
  <Route
    key="manage-tariff-redirect"
    path={`${portalRoutes.manageDevice.base}/tariff/*`}
    element={<LegacyTariffRedirect />}
  />,
  /*
    The checkout moved from `/portal/manage/subscription/checkout` to
    `/portal/manage/subscription/change/checkout` when the subscription-change
    flow gained its own shell. This redirect keeps payment-gateway return URLs
    (which still point to the old path) working by forwarding them to the new
    location, query string preserved.
  */
  <Route
    key="subscription-checkout-redirect"
    path={`${portalRoutes.manageDevice.subscription.base}/checkout`}
    element={<LegacySubscriptionCheckoutRedirect />}
  />,
  <Route
    key={portalRoutes.manageDevice.base}
    path={portalRoutes.manageDevice.base}
    element={<DeviceManagement />}
  >
    <Route
      index
      element={
        <NavigateWithQueryParams
          to={portalRoutes.manageDevice.subscription.base}
        />
      }
    />
    <Route
      path={portalRoutes.manageDevice.numberPorting}
      element={<NumberPorting missingInfo />}
    />
    <Route path={portalRoutes.manageDevice.subscription.base}>
      <Route
        index
        element={
          <NavigateWithQueryParams
            to={portalRoutes.manageDevice.subscription.display}
          />
        }
      />
      <Route
        path={portalRoutes.manageDevice.subscription.display}
        element={<SubscriptionDisplay />}
      />
      <Route
        path={portalRoutes.manageDevice.subscription.change}
        element={<SubscriptionChange />}
      >
        <Route index element={<SubscriptionRateSelection />} />
        <Route path="checkout" element={<SubscriptionCheckout />} />
      </Route>
      <Route
        path={portalRoutes.manageDevice.subscription.automaticRenewal}
        element={<SubscriptionAutoRenewal />}
      />
    </Route>
    <Route path={portalRoutes.manageDevice.roaming.base}>
      <Route
        index
        element={
          <NavigateWithQueryParams
            to={portalRoutes.manageDevice.roaming.display}
          />
        }
      />
      <Route
        path={portalRoutes.manageDevice.roaming.display}
        element={<ShowRoamingOptions />}
      />
      <Route
        path={portalRoutes.manageDevice.roaming.checkout}
        element={<CheckoutRoamingOption />}
      />
      <Route
        path={portalRoutes.manageDevice.roaming.booking}
        element={<AddRoamingOption />}
      />
    </Route>
    <Route path={portalRoutes.manageDevice.voice} element={<VoiceSettings />} />
    <Route path={portalRoutes.manageDevice.watchSim.base}>
      <Route
        index
        element={
          <NavigateWithQueryParams
            to={portalRoutes.manageDevice.watchSim.display}
          />
        }
      />
      <Route
        path={portalRoutes.manageDevice.watchSim.display}
        element={<ShowWatchSimOptions />}
      />
      <Route
        path={portalRoutes.manageDevice.watchSim.checkout}
        element={<AddWatchSimOption />}
      />
      <Route
        path={portalRoutes.manageDevice.watchSim.automaticRenewal}
        element={<ChangeWatchSimRenewal />}
      />
    </Route>

    <Route path={portalRoutes.manageDevice.details.base}>
      <Route
        index
        element={
          <NavigateWithQueryParams
            to={portalRoutes.manageDevice.details.display}
          />
        }
      />
      <Route path={portalRoutes.manageDevice.details.display}>
        <Route index element={<DetailsDisplay />} />
      </Route>
    </Route>
  </Route>,
  <Route
    key={portalRoutes.mySubscriptions.base}
    path={portalRoutes.mySubscriptions.base}
  >
    <Route index element={<MySubscriptions />} />
    <Route
      path={portalRoutes.mySubscriptions.tiles}
      element={<Navigate to={portalRoutes.mySubscriptions.base} replace />}
    />
    <Route
      path={portalRoutes.mySubscriptions.list}
      element={<MySubscriptionsList />}
    />
  </Route>,
  <Route
    key={portalRoutes.addDevice.base}
    path={portalRoutes.addDevice.base}
    element={<AddDevice />}
  >
    <Route index element={<Navigate to={portalRoutes.addDevice.step1} />} />
    <Route path={portalRoutes.addDevice.step1}>
      <Route index element={<Navigate to="sim-selection" replace />} />
      <Route
        path={portalRoutes.addDevice.simSelection}
        element={<SimSelection />}
      />
      <Route
        path={portalRoutes.addDevice.simVerification}
        element={<SimVerification />}
      />
    </Route>
    <Route path={portalRoutes.addDevice.step2}>
      <Route
        index
        element={<Navigate to={portalRoutes.addDevice.rateSelection} replace />}
      />
      <Route
        path={portalRoutes.addDevice.rateSelection}
        element={<RateSelection />}
      />
    </Route>
    <Route path={portalRoutes.addDevice.step3}>
      <Route
        index
        element={<Navigate to={portalRoutes.addDevice.numberPorting} replace />}
      />
      <Route
        path={portalRoutes.addDevice.numberPorting}
        element={<NumberPorting />}
      />
    </Route>
    <Route path={portalRoutes.addDevice.step4}>
      <Route
        index
        element={<Navigate to={portalRoutes.addDevice.checkout} replace />}
      />
      <Route path={portalRoutes.addDevice.checkout} element={<Checkout />} />
    </Route>
  </Route>,
  <Route
    key={portalRoutes.addDevice.success}
    path={portalRoutes.addDevice.success}
    element={<AddDeviceSuccess />}
  />,
  <Route key={portalRoutes.bulkSimActivation} element={<AgentOnlyRoute />}>
    <Route
      path={portalRoutes.bulkSimActivation}
      element={<BulkSimActivation />}
    />
  </Route>,
  <Route
    key={portalRoutes.user.base}
    path={portalRoutes.user.base}
    element={<UserAccountView />}
  >
    <Route
      index
      element={<Navigate to={portalRoutes.user.personal} replace />}
    />
    <Route path={portalRoutes.user.personal} element={<PersonalDetails />} />
    <Route path={portalRoutes.user.payments} element={<PaymentMethods />} />
    <Route path={portalRoutes.user.inbox} element={<Inbox />} />
    {/*
      Security/Login tab. `SecurityView` is a layout that always renders the
      E-Mail/Passwort/Magic-Link cards and an <Outlet/> for the 2FA section, so
      the whole 2FA flow (status -> method picker -> setup steps) stays inline
      on the page instead of replacing it.
    */}
    <Route path={portalRoutes.user.security.base} element={<SecurityView />}>
      <Route index element={<TwoFactorAuthStatus />} />
      <Route
        path={portalRoutes.user.security.initialSetup}
        element={<TwoFactorInitialSetupView />}
      />
      <Route
        path={portalRoutes.user.security.initialSmsSetup}
        element={<TwoFactorInitialSmsSetupView />}
      />
      <Route
        path={portalRoutes.user.security.initialSmsConfirm}
        element={<TwoFactorInitialSmsConfirmView />}
      />
      <Route
        path={portalRoutes.user.security.initialAppSetup}
        element={<TwoFactorInitialAppSetupView />}
      />
      <Route
        path={portalRoutes.user.security.confirmBackupCodeReception}
        element={<TwoFactorBackupConfirmView />}
      />
      <Route
        path={portalRoutes.user.security.confirmStandard}
        element={<TwoFactorConfirmStandardView />}
      />
      <Route
        path={portalRoutes.user.security.verify2faSms}
        element={<TwoFactorConfirmSmsView />}
      />
      {/* INFO: These changes require the verification via 2FA token, which we get via the TwoFactorCodeModal */}
      <Route
        element={
          <TwoFactorChangeProtection>
            <Outlet />
          </TwoFactorChangeProtection>
        }
      >
        <Route
          path={portalRoutes.user.security.change2faSms}
          element={<TwoFactorChangeSmsView />}
        />
        <Route
          path={portalRoutes.user.security.change2faApp}
          element={<TwoFactorChangeAppView />}
        />
        <Route
          path={portalRoutes.user.security.setup2faSms}
          element={<TwoFactorSetupSmsView />}
        />
        <Route
          path={portalRoutes.user.security.verify2faNumberChange}
          element={<TwoFactorConfirmNumberChange />}
        />
        <Route
          path={portalRoutes.user.security.setup2faApp}
          element={<TwoFactorSetupAppView />}
        />
      </Route>
    </Route>
  </Route>,
  <Route
    key={portalRoutes.documents.base}
    path={portalRoutes.documents.base}
    element={<Documents />}
  >
    <Route
      index
      element={<Navigate to={portalRoutes.documents.bills} replace />}
    />
    <Route
      path={portalRoutes.documents.itemizedBills}
      element={<ItemizedBillOverview />}
    />
    <Route path={portalRoutes.documents.bills} element={<BillList />} />
    <Route
      path={portalRoutes.documents.payments}
      element={<PaymentMethods />}
    />
  </Route>,
  // Outside the Documents shell: the checkout-style page has no tabs.
  <Route
    key={portalRoutes.documents.bulkMove}
    path={portalRoutes.documents.bulkMove}
    element={<BulkMoveSubscriptions />}
  />,
  <Route
    key={portalRoutes.friendsWithBenefits.base}
    path={portalRoutes.friendsWithBenefits.base}
  >
    <Route element={<FriendsWithBenefitsRoute />}>
      <Route
        index
        element={
          <Navigate
            to={portalRoutes.friendsWithBenefits.groupOverview}
            replace
          />
        }
      />
      <Route
        path={portalRoutes.friendsWithBenefits.groupOverview}
        element={<GroupOverview />}
      />
    </Route>
    <Route
      path={portalRoutes.friendsWithBenefits.createGroup}
      element={<CreateGroup />}
    />
  </Route>,
  <Route
    key={portalRoutes.initialPasswordSetup}
    path={portalRoutes.initialPasswordSetup}
    element={<InitialPasswordSetup />}
  />,
  <Route
    key={portalRoutes.personalDataOnboarding}
    path={portalRoutes.personalDataOnboarding}
    element={<PersonalDataOnboarding />}
  />,
  <Route
    key={portalRoutes.notFound}
    path={portalRoutes.notFound}
    element={<Navigate to="/404" replace />}
  />,
];
